import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import SectionCard from "./SectionCard";
import Select_v2 from "./Select_v2";
import { OrgContext } from "../../context/OrgContext";
import { WarehouseContext } from "../../context/WarehouseContext";
import { LineHaulApi } from "../../api/LineHaulApi";
import {
  buildLinehaulTariffPayload,
  extractLinehaulTariffLosId,
  normalizeLosOptionsList,
  resolveLinehaulSpecialFlag,
  resolveLinehaulTariffFromAccountConfig,
  resolveLinehaulTariffFromServiceLevel,
  resolveLinehaulTariffFromTracking,
  resolveLinehaulTariffId,
  resolveLinehaulTariffIdFromLosId,
  isLinehaulTariffLockedByServiceLevel,
} from "../../helpers/serviceLevelDerivation";
import { useModeBLinehaulTariffOptions } from "../../helpers/useModeBLinehaulTariffOptions";
import { Col, Row, Select, Typography, Switch, DatePicker, TimePicker, Icon, Checkbox, Tooltip, Spin } from "antd";
import {
  LinehaulTrackIcon,
  LhPillIcon,
  LhBadgeCompleteIcon,
  LhBadgeInTransitIcon,
  LhBadgePendingIcon
} from "../../common/CustomIcon";
import { alertMessage } from "../../common/Common";
import { checkServiceExistance } from "../../helpers/common";
import moment from "moment";
import {
  buildStops,
  buildStopsFromLane,
  getCompletedRouteCodes,
  isLastRoutingLegComplete,
  isLegDateLocked,
  overlayTrackingOnLaneStops,
  renderRoutingPathLabel,
  formatCalendarDisplay,
  buildLegDatesFromTerminals,
  resolveHopDateKeyForLeg,
  filterLhWarehouseTerminals,
  resolveLegLinehaulServiceDate,
  resolveRoutingLegKey,
  shouldShowLegDatePill,
  resolvePickupAppointmentDate,
  resolveFirstLegDateFromPickup,
  resolveLegDateMinSelectable,
  Stop,
  LegConnector,
} from "./LhTrackingNew/LhTrackingCard";
import {
  getCompletedPrefixCodes,
  getRemainingRouteCodes,
  hasInTransitRoutingStop,
  remainingLaneLockReason,
  resolveCurrentLaneOriginId,
  prefixLanePaths,
  withCurrentTrackingPath,
} from "./LhTrackingNew/linehaulRemainingLanes";
import "./LhTrackingNew/TrackingStyles.scss";

const { Text } = Typography;

const LEG_STATUS = {
  pending: { label: "Pending", icon: LhBadgePendingIcon, color: "#1A354D" },
  arrived: { label: "Arrived", icon: LhBadgeCompleteIcon, color: "#CC9200" },
  in_transit: { label: "In Transit", icon: LhBadgeInTransitIcon, color: "#4559B0" },
  complete: { label: "Complete", icon: LhBadgeCompleteIcon, color: "#047857" }
};

const STATUS_KEY = {
  "PENDING": "pending",
  "IN TRANSIT": "in_transit",
  "ARRIVED": "arrived",
  "COMPLETED": "complete",
  "ASSIGNED": "in_transit",
};

const DAY_MAP = {
  MON: "MONDAY", MONDAY: "MONDAY",
  TUE: "TUESDAY", TUES: "TUESDAY", TUESDAY: "TUESDAY",
  WED: "WEDNESDAY", WEDNESDAY: "WEDNESDAY",
  THU: "THURSDAY", THUR: "THURSDAY", THURS: "THURSDAY", THURSDAY: "THURSDAY",
  FRI: "FRIDAY", FRIDAY: "FRIDAY",
  SAT: "SATURDAY", SATURDAY: "SATURDAY",
  SUN: "SUNDAY", SUNDAY: "SUNDAY"
};

const normalizeOpDays = (days = []) => {
  if (!Array.isArray(days)) return [];
  return days
    .map((d) => DAY_MAP[String(d).trim().toUpperCase()] || String(d).trim().toUpperCase())
    .filter(Boolean);
};

const toStartOfDay = (value) => {
  if (!value) return null;
  if (typeof value === "string") {
    const ymd = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (ymd) {
      const parsed = moment(ymd[1], "YYYY-MM-DD", true);
      return parsed.isValid() ? parsed.startOf("day") : null;
    }
  }
  if (moment.isMoment(value) && value.isValid()) {
    const parsed = moment(value.format("YYYY-MM-DD"), "YYYY-MM-DD", true);
    return parsed.isValid() ? parsed.startOf("day") : null;
  }
  const parsed = moment.parseZone(value);
  if (!parsed.isValid()) return null;
  const ymd = parsed.format("YYYY-MM-DD");
  const day = moment(ymd, "YYYY-MM-DD", true);
  return day.isValid() ? day.startOf("day") : null;
};

const formatCalendarDate = (value) => {
  const parsed = toStartOfDay(value);
  return parsed ? parsed.format("YYYY-MM-DD") : null;
};

const getDateFloor = (...candidates) => {
  const today = moment().startOf("day");
  let floor = today.clone();
  candidates.forEach((candidate) => {
    const parsed = toStartOfDay(candidate);
    if (parsed && parsed.isAfter(floor)) {
      floor = parsed;
    }
  });
  return floor;
};

const getEditOrderId = (orderResponse) => {
  const fromOrder = orderResponse?.id || orderResponse?.customer_order_id;
  if (fromOrder) return String(fromOrder);
  if (typeof window === "undefined") return null;
  const match = window.location.pathname.match(/\/orders\/([^/]+)\/edit(?:\/|$)/);
  return match?.[1] || null;
};

const resolveTrackingPathId = (tracking) =>
  tracking?.lh_routing_path_id || tracking?.path_id || null;

const LegDatePillPicker = ({ value, onChange, disabled, operationalDays = [], minDate }) => {
  const [open, setOpen] = useState(false);
  const calendarValue = toStartOfDay(value);
  const displayDate = formatCalendarDisplay(value);
  const minSelectable = resolveLegDateMinSelectable(minDate);

  // Ant Design renders the calendar on document.body. Create Order scrolls an
  // inner container, so the popup would otherwise stay on screen while the
  // date pill moves. Close it on any ancestor scroll.
  useEffect(() => {
    if (!open) return undefined;

    const closeOnScroll = (event) => {
      if (event.target?.closest?.(".lh-calendar-popover")) {
        return;
      }
      setOpen(false);
    };

    window.addEventListener("scroll", closeOnScroll, true);
    return () => window.removeEventListener("scroll", closeOnScroll, true);
  }, [open]);

  if (disabled) {
    return (
      <span className="lh-leg-date-pill is-disabled">
        <Icon type="calendar" style={{ fontSize: "11px", color: "#AABDCD" }} />
        <span>{displayDate}</span>
      </span>
    );
  }

  const upperOpDays = normalizeOpDays(operationalDays);
  const hasNoOpDays = upperOpDays.length === 0;

  const disabledDate = (currentDate) => {
    if (!currentDate) return false;
    return currentDate.isBefore(minSelectable, "day");
  };

  const dateRender = (currentDate) => {
    if (!currentDate) return null;
    const isBeforeMin = currentDate.isBefore(minSelectable, "day");
    const dayOfWeek = currentDate.format("dddd").toUpperCase();
    const isOperational = !hasNoOpDays && !isBeforeMin && upperOpDays.includes(dayOfWeek);
    const isSelected = calendarValue && currentDate.isSame(calendarValue, "day");

    let dayClass = "lh-cal-day";
    if (isSelected) {
      dayClass += " is-selected";
    } else if (isOperational) {
      dayClass += " is-operational";
    } else if (isBeforeMin) {
      dayClass += " is-disabled";
    }

    return (
      <div className="lh-cal-date">
        <span className={dayClass}>
          {currentDate.date()}
        </span>
      </div>
    );
  };

  return (
    <div className="lh-leg-date-pill-wrap" style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        className="lh-leg-date-pill"
        onClick={() => setOpen(true)}
      >
        <Icon type="calendar" style={{ fontSize: "11px", color: "#8AA5B9" }} />
        <span>{displayDate}</span>
      </button>

      <DatePicker
        open={open}
        onOpenChange={(status) => setOpen(status)}
        value={calendarValue}
        onChange={(date) => {
          if (date) onChange(toStartOfDay(date) || date);
          setOpen(false);
        }}
        disabledDate={disabledDate}
        dateRender={dateRender}
        defaultPickerValue={calendarValue || minSelectable}
        showToday={false}
        renderExtraFooter={hasNoOpDays ? () => (
          <div className="lh-cal-no-op-days">
            <Icon type="info-circle" />
            <span>No operational days for this leg</span>
          </div>
        ) : undefined}
        dropdownClassName={`lh-calendar-popover${hasNoOpDays ? " has-no-op-days" : ""}`}
        format="MM/DD/YYYY"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 0,
          height: 0,
          opacity: 0,
          pointerEvents: "none"
        }}
      />
    </div>
  );
};

const LineHaulCreateForm = ({
  locations,
  disabled,
  onSelectPath,
  initialPathId,
  pickupDate,
  pickupWarehouseId,
  deliveryWarehouseId: propDeliveryWarehouseId,
  // The order's serving warehouse. Distinct from pickupWarehouseId (the PICKUP location's
  // warehouse) — only PLH, which terminates at the serving warehouse, needs it.
  orderWarehouseId,
  orderTypeCode,
  orderResponse,
  losOptions: losOptionsProp = [],
  levelOfServices = [],
  serviceLevelLegLos = null,
  accountId,
  initialLos,
  onSelectLos,
  onChangeTracking,
  onChangeSpecial,
  serviceLevelFlowEnabled: serviceLevelFlowEnabledProp,
  accountBillingConfig = {},
  orgBillingRules = {},
}) => {
  const { currentOrg, orgSettings = {} } = useContext(OrgContext);
  const serviceLevelFlowEnabled = serviceLevelFlowEnabledProp ?? (
    orgSettings?.service_level_flow_enabled === "true"
  );
  const { warehouseHash } = useContext(WarehouseContext);
  const [lanePaths, setLanePaths] = useState([]);
  const [selectedPath, setSelectedPath] = useState(initialPathId || null);
  const [originWarehouseId, setOriginWarehouseId] = useState(null);
  const [destinationWarehouseId, setDestinationWarehouseId] = useState(null);
  const lastLaneFetchKeyRef = useRef("");
  const lanePathsRef = useRef([]);
  const lastZipFetchKeyRef = useRef("");
  const lastTrackingPayloadRef = useRef("");
  const [userTouchedLegDates, setUserTouchedLegDates] = useState(false);
  const manualFirstLegEditRef = useRef(false);
  const lastPickupSyncedKeyRef = useRef(null);
  const prevPickupDateKeyRef = useRef(undefined);
  const appliedApiLegDatesRef = useRef(false);
  const savedLegDatesSnapshotRef = useRef({});
  const initialPickupDateKeyRef = useRef(null);
  const [fetchingLanes, setFetchingLanes] = useState(false);
  const prevOrderTypeRef = useRef(orderTypeCode);

  if (prevOrderTypeRef.current !== orderTypeCode) {
    prevOrderTypeRef.current = orderTypeCode;
    lastLaneFetchKeyRef.current = "";
    lastZipFetchKeyRef.current = "";
  }

  const [isCustomServiceDate, setIsCustomServiceDate] = useState(false);
  const [serviceDate, setServiceDate] = useState(null);
  const [serviceTime, setServiceTime] = useState(null);
  const [selectedLeg, setSelectedLeg] = useState(null);
  const [lhSpecial, setLhSpecial] = useState(() => resolveLinehaulSpecialFlag({
    tracking: orderResponse?.linehaul_tracking_info,
    order: orderResponse,
  }));
  const pickupLocation = locations?.find(
    (loc) => loc.type_of_loc === "PICKUP"
  );
  const pickupDateMoment = toStartOfDay(
    resolvePickupAppointmentDate(pickupLocation, pickupDate)
  );
  const pickupDateKey = pickupDateMoment ? pickupDateMoment.format("YYYY-MM-DD") : "";
  const baseLosOptions = useMemo(
    () => (losOptionsProp.length ? losOptionsProp : levelOfServices),
    [losOptionsProp, levelOfServices]
  );
  const orderLinehaulTariffId = extractLinehaulTariffLosId(orderResponse);
  const {
    effectiveLosOptions: losOptions,
    modeBDefaultTariffId,
    modeBDefaultReady,
    effectiveLosOptionsKey: losOptionsKey,
  } = useModeBLinehaulTariffOptions({
    serviceLevelFlowEnabled,
    baseOptions: baseLosOptions,
    accountBillingConfig,
    orgBillingRules,
    savedLinehaulTariffId: orderLinehaulTariffId,
  });
  const slLegLosKey = useMemo(
    () => JSON.stringify(serviceLevelLegLos || []),
    [serviceLevelLegLos]
  );

  const [selectedTariffId, setSelectedTariffId] = useState(undefined);
  const userTouchedTariffRef = useRef(false);
  const prevAccountIdRef = useRef(accountId);
  // First populated service-level legs are hydration (keep a saved order tariff).
  // A later change is the user switching service levels — re-derive the tariff
  // and keep doing so until they pick a tariff themselves.
  const seenSlLegsRef = useRef(false);
  const prevSlLegLosKeyRef = useRef(slLegLosKey);
  const slDrivenTariffRef = useRef(false);

  const [legDatesMap, setLegDatesMap] = useState({});
  const [trackingDetails, setTrackingDetails] = useState(null);
  const trackingOrderId = getEditOrderId(orderResponse);
  const [trackingReady, setTrackingReady] = useState(() => !getEditOrderId(orderResponse));

  useEffect(() => {
    if (!trackingOrderId) {
      setTrackingReady(true);
      return undefined;
    }
    let cancelled = false;
    setTrackingReady(false);
    LineHaulApi.fetchLineHaulRealTimeTracking(trackingOrderId)
      .then((res) => {
        if (cancelled) return;
        setTrackingDetails(res?.success && res?.data ? res.data : null);
        setTrackingReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setTrackingDetails(null);
        setTrackingReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [trackingOrderId]);

  useEffect(() => {
    setLhSpecial(resolveLinehaulSpecialFlag({
      tracking: trackingDetails || orderResponse?.linehaul_tracking_info,
      order: orderResponse,
    }));
  }, [
    orderResponse?.id,
    orderResponse?.linehaul_special,
    orderResponse?.customer_order_details?.linehaul_special,
    orderResponse?.is_linehaul_special,
    orderResponse?.customer_order_details?.is_linehaul_special,
    trackingDetails?.linehaul_special,
  ]);

  useEffect(() => {
    if (prevAccountIdRef.current === accountId) return;
    const prevAccountId = prevAccountIdRef.current;
    prevAccountIdRef.current = accountId;
    // Skip initial hydration (undefined/"" -> loaded id); that is not a user account change.
    if (!prevAccountId || !accountId) return;
    // Keep saved order tariff when re-entering edit — applySavedTariff does not list accountId.
    if (orderLinehaulTariffId && getEditOrderId(orderResponse)) return;
    userTouchedTariffRef.current = false;
    setSelectedTariffId(undefined);
  }, [accountId, orderLinehaulTariffId, orderResponse]);

  useEffect(() => {
    const slHasLegs = Array.isArray(serviceLevelLegLos) && serviceLevelLegLos.length > 0;
    const slKeyChanged = prevSlLegLosKeyRef.current !== slLegLosKey;
    // Edit hydration: the order's own SL legs arrive once and must not overwrite
    // a tariff already saved on the order. Every other legs change is a new
    // service level and replaces the dropdown selection.
    const initialSavedHydration = slHasLegs && slKeyChanged && !seenSlLegsRef.current && !!orderLinehaulTariffId;
    const slDrivesTariff = slHasLegs && slKeyChanged && !initialSavedHydration;
    if (slHasLegs) seenSlLegsRef.current = true;
    prevSlLegLosKeyRef.current = slLegLosKey;
    if (slDrivesTariff) {
      userTouchedTariffRef.current = false;
      slDrivenTariffRef.current = true;
    } else if (userTouchedTariffRef.current) {
      return;
    }

    if (slDrivenTariffRef.current && serviceLevelFlowEnabled) {
      if (!losOptions.length) return;
      const fromSl = resolveLinehaulTariffFromServiceLevel(serviceLevelLegLos, losOptions);
      const fromAccount = fromSl?.id ? null : resolveLinehaulTariffFromAccountConfig(losOptions);
      const nextId = fromSl?.id || fromAccount?.id;
      setSelectedTariffId(nextId ? String(nextId) : undefined);
      return;
    }

    const applySavedTariff = () => {
      if (orderLinehaulTariffId) {
        const fromOrderTariffId = resolveLinehaulTariffIdFromLosId(orderLinehaulTariffId, losOptions);
        setSelectedTariffId(fromOrderTariffId || String(orderLinehaulTariffId));
        return true;
      }
      if (trackingDetails) {
        const resolved = resolveLinehaulTariffFromTracking(trackingDetails, losOptions);
        if (resolved?.id) {
          setSelectedTariffId(String(resolved.id));
          return true;
        }
      }
      return false;
    };

    if (applySavedTariff()) return;

    if (serviceLevelFlowEnabled) {
      if (!losOptions.length) {
        return;
      }
      setSelectedTariffId((current) => {
        if (current && losOptions.some((opt) => String(opt.id) === String(current))) {
          return String(current);
        }
        const nextId = resolveLinehaulTariffId({
          tracking: orderResponse?.linehaul_tracking_info || {},
          serviceLevelLegLos,
          losOptions,
          initialCode: initialLos,
          linehaulTariffId: orderLinehaulTariffId,
        });
        return nextId ? String(nextId) : undefined;
      });
      return;
    }

    if (!modeBDefaultReady || !modeBDefaultTariffId) return;
    setSelectedTariffId(String(modeBDefaultTariffId));
  }, [
    losOptionsKey,
    slLegLosKey,
    orderLinehaulTariffId,
    trackingDetails,
    serviceLevelFlowEnabled,
    modeBDefaultReady,
    modeBDefaultTariffId,
    accountBillingConfig,
    accountId,
  ]);

  useEffect(() => {
    if (userTouchedLegDates || appliedApiLegDatesRef.current) return;

    const allPaths = prefixLanePaths(
      lanePaths.length > 0 ? lanePaths : transformPaths(orderResponse?.paths || []),
      trackingDetails?.terminals,
      orderTypeCode,
      getCompletedRouteCodes(buildStops(trackingDetails || {}, orderTypeCode || ""))
    );
    const trackingPathId = resolveTrackingPathId(trackingDetails);
    const currentPathObj = allPaths.find((p) => String(p.path_id || p._id || p.id) === String(selectedPath || trackingPathId)) || allPaths[0];
    if (!currentPathObj || !Array.isArray(currentPathObj.legs) || currentPathObj.legs.length === 0) {
      return;
    }
    const savedLegs = trackingDetails?.legs || [];
    const legsForDates = savedLegs.length > 0 ? savedLegs : currentPathObj.legs;
    const terminalDates = buildLegDatesFromTerminals(trackingDetails?.terminals, legsForDates);

    if (Object.keys(terminalDates).length > 0) {
      appliedApiLegDatesRef.current = true;
      const snapshot = {};
      Object.entries(terminalDates).forEach(([key, value]) => {
        snapshot[key] = formatCalendarDate(value);
      });
      savedLegDatesSnapshotRef.current = snapshot;
      setLegDatesMap(terminalDates);
    }
  }, [selectedPath, lanePaths, orderResponse?.paths, trackingDetails, userTouchedLegDates]);

  useEffect(() => {
    const allPaths = prefixLanePaths(
      lanePaths.length > 0 ? lanePaths : transformPaths(orderResponse?.paths || []),
      trackingDetails?.terminals,
      orderTypeCode,
      getCompletedRouteCodes(buildStops(trackingDetails || {}, orderTypeCode || ""))
    );
    const trackingPathId = resolveTrackingPathId(trackingDetails);
    const currentPathObj = allPaths.find((p) => String(p.path_id || p._id || p.id) === String(selectedPath || trackingPathId)) || allPaths[0];
    const firstLeg = currentPathObj?.legs?.[0];
    if (!firstLeg) return;

    const isInitialRun = prevPickupDateKeyRef.current === undefined;
    const pickupChanged = !isInitialRun && prevPickupDateKeyRef.current !== pickupDateKey;
    prevPickupDateKeyRef.current = pickupDateKey;

    const savedLegs = trackingDetails?.legs || [];
    const legsForDates = savedLegs.length > 0 ? savedLegs : currentPathObj.legs;
    const terminalDates = buildLegDatesFromTerminals(trackingDetails?.terminals, legsForDates);
    const hasSavedLeg1 = Boolean(terminalDates[1] || savedLegs[0]?.linehaul_service_date);

    if (isInitialRun) {
      if (pickupDateKey && initialPickupDateKeyRef.current == null) {
        initialPickupDateKeyRef.current = pickupDateKey;
      }
      if (hasSavedLeg1) {
        if (pickupDateKey) lastPickupSyncedKeyRef.current = pickupDateKey;
        return;
      }
    }

    if (
      initialPickupDateKeyRef.current
      && pickupDateKey === initialPickupDateKeyRef.current
      && savedLegDatesSnapshotRef.current[1]
    ) {
      manualFirstLegEditRef.current = false;
      lastPickupSyncedKeyRef.current = pickupDateKey;
      const restored = toStartOfDay(savedLegDatesSnapshotRef.current[1]);
      if (restored) {
        setLegDatesMap((prev) => ({ ...prev, 1: restored }));
      }
      return;
    }

    if (pickupChanged) {
      manualFirstLegEditRef.current = false;
      lastPickupSyncedKeyRef.current = null;
    }

    if (manualFirstLegEditRef.current) return;

    if (!pickupDateMoment) {
      lastPickupSyncedKeyRef.current = null;
      setLegDatesMap((prev) => {
        if (!prev[1]) return prev;
        const next = { ...prev };
        delete next[1];
        return next;
      });
      return;
    }

    if (!pickupChanged && lastPickupSyncedKeyRef.current === pickupDateKey) return;

    const nextDate = resolveFirstLegDateFromPickup(pickupDateMoment, firstLeg);
    if (!nextDate) return;

    lastPickupSyncedKeyRef.current = pickupDateKey;
    setLegDatesMap((prev) => ({ ...prev, 1: nextDate }));
  }, [pickupDateKey, selectedPath, lanePaths, orderResponse?.paths, trackingDetails]);

  const hasLinehaulTrackingPresent = () => {
    const trackingForStops = trackingDetails || orderResponse?.linehaul_tracking_info || {};
    return Boolean(
      (Array.isArray(trackingDetails?.terminals) && trackingDetails.terminals.length > 0)
      || (Array.isArray(orderResponse?.paths) && orderResponse.paths.length > 0)
      || buildStops(trackingForStops, orderTypeCode || "").length > 0
      || resolveTrackingPathId(trackingDetails)
    );
  };

  const applyFetchedLanes = (rawPaths = [], { showApiWarning = null } = {}) => {
    const savedPathId = resolveTrackingPathId(trackingDetails) || initialPathId;
    const remainingCodes = getRemainingRouteCodes(trackingDetails?.terminals, orderTypeCode);
    const prefixFallback = getCompletedRouteCodes(
      buildStops(trackingDetails || orderResponse?.linehaul_tracking_info || {}, orderTypeCode || "")
    );
    const fromApi = transformPaths(Array.isArray(rawPaths) ? rawPaths : []);
    const fromOrder = transformPaths(orderResponse?.paths || []);
    const base = fromApi.length ? fromApi : fromOrder;
    const prefixed = prefixLanePaths(
      base,
      trackingDetails?.terminals,
      orderTypeCode,
      prefixFallback
    );
    const filtered = prefixed.filter((path) => {
      const start = (path.remainingCodes || [])[0];
      if (!remainingCodes[0] || !start) return true;
      return String(start).toUpperCase() === String(remainingCodes[0]).toUpperCase();
    });
    let candidate = filtered.length ? filtered : prefixed;
    let { paths, preferred } = withCurrentTrackingPath(
      candidate,
      trackingDetails?.terminals,
      orderTypeCode,
      savedPathId
    );
    if (!paths.length && hasLinehaulTrackingPresent() && fromOrder.length) {
      ({ paths, preferred } = withCurrentTrackingPath(
        prefixLanePaths(fromOrder, trackingDetails?.terminals, orderTypeCode, prefixFallback),
        trackingDetails?.terminals,
        orderTypeCode,
        savedPathId
      ));
    }
    if (!paths.length && hasLinehaulTrackingPresent() && lanePathsRef.current.length > 0) {
      return;
    }
    setLanePaths(paths);
    lanePathsRef.current = paths;
    let nextId = preferred?.path_id != null && preferred?.path_id !== ""
      ? String(preferred.path_id)
      : null;
    if (!nextId && savedPathId && paths.some((p) => String(p.path_id) === String(savedPathId))) {
      nextId = String(savedPathId);
    }
    if (!nextId && paths[0]?.path_id != null) {
      nextId = String(paths[0].path_id);
    }
    if (nextId != null) {
      setSelectedPath(nextId);
      if (onSelectPath) onSelectPath(nextId);
    }
    if (showApiWarning && !fromApi.length) {
      alertMessage(showApiWarning, "warning");
    }
  };

  const transformPaths = (rawPaths) => {
    return rawPaths.map((path) => {
      let routeCodes = [];
      if (Array.isArray(path.legs) && path.legs.length > 0) {
        routeCodes = [path.legs[0].origin_code, ...path.legs.map((l) => l.destination_code)];
      } else if (Array.isArray(path.stops) && path.stops.length > 0) {
        routeCodes = path.stops.map((stopId) => warehouseHash[stopId]?.code || warehouseHash[stopId]?.name || stopId);
      } else {
        routeCodes = String(path.path_name || path.route || "").split("-").map((c) => c.trim());
      }

      return {
        path_id: path.path_id || path._id || path.id,
        route: path.path_name || routeCodes.join("-"),
        routeCodes,
        is_primary: Boolean(path.is_primary),
        stops: path.stops || routeCodes,
        legs: path.legs || []
      };
    });
  };

  useEffect(() => {
    const details = orderResponse?.customer_order_details || orderResponse;
    if (details?.custom_service_date || details?.service_date) {
      setIsCustomServiceDate(true);
      const sDate = details.custom_service_date || details.service_date;
      setServiceDate(moment(sDate));
      const sTime = details.service_time;
      if (sTime) {
        setServiceTime(moment(sTime, "hh:mm A"));
      }
    }
  }, [orderResponse]);

  useEffect(() => {
    if (initialPathId && onSelectPath) {
      onSelectPath(initialPathId);
    }
  }, []);

  const deliveryLocation = locations?.find(
    (loc) => loc.type_of_loc === "DELIVERY"
  );

  const originWhId = pickupLocation?.warehouse_id;
  const destinationWhId = deliveryLocation?.warehouse_id;
  const originZipcode = pickupLocation?.customer_zipcode;
  const destinationZipcode = deliveryLocation?.customer_zipcode;
  const isWarehouseToWarehouse = orderTypeCode === "LH" || orderTypeCode === "NLH";

  // Explicit props / zip-resolved ids beat location-ref values, which can lag one render.
  const resolveDestinationWarehouseId = () =>
    propDeliveryWarehouseId || destinationWarehouseId || destinationWhId || null;

  const resolveOriginWarehouseId = () =>
    resolveCurrentLaneOriginId(
      trackingDetails?.terminals,
      pickupWarehouseId || originWarehouseId || originWhId || null,
      warehouseHash,
      orderTypeCode
    );

  const resolveLaneWarehouseIds = () => {
    const destId = resolveDestinationWarehouseId();
    if (orderTypeCode === "PLH") {
      return {
        originId: resolveOriginWarehouseId(),
        // PLH terminates at the order's serving warehouse, not at a delivery location.
        destId: destId || orderWarehouseId || pickupWarehouseId
      };
    }
    return {
      originId: resolveOriginWarehouseId(),
      destId
    };
  };

  useEffect(() => {
    if (!propDeliveryWarehouseId) return;
    setDestinationWarehouseId((prev) => (
      String(prev || "") === String(propDeliveryWarehouseId) ? prev : propDeliveryWarehouseId
    ));
  }, [propDeliveryWarehouseId]);

  const fetchWarehousesByZipcode = async (originzipcode, destinationzipcode) => {
    try {
      const result = await LineHaulApi.fetchWarehousesByZipcode(
        currentOrg.id,
        originzipcode,
        destinationzipcode
      );

      if (result.success) {
        setOriginWarehouseId(result.data?.origin_warehouse || null);
        setDestinationWarehouseId(result.data?.destination_warehouse || null);
      }
    } catch (err) {
      console.error("Failed to resolve warehouses by zipcode:", err);
    }
  };

  useEffect(() => {
    const { originId, destId } = resolveLaneWarehouseIds();
    if (originId && destId) return;

    if (originWhId && destinationWhId && isWarehouseToWarehouse) {
      setOriginWarehouseId((prev) => (prev === originWhId ? prev : originWhId));
      setDestinationWarehouseId((prev) => (prev === destinationWhId ? prev : destinationWhId));
      return;
    }

    if (!currentOrg?.id || !originZipcode || !destinationZipcode) return;
    const zipKey = `${currentOrg.id}|${originZipcode}|${destinationZipcode}`;
    if (lastZipFetchKeyRef.current === zipKey) return;
    lastZipFetchKeyRef.current = zipKey;
    fetchWarehousesByZipcode(originZipcode, destinationZipcode);
  }, [
    originWhId,
    destinationWhId,
    originZipcode,
    destinationZipcode,
    pickupWarehouseId,
    propDeliveryWarehouseId,
    originWarehouseId,
    destinationWarehouseId,
    orderWarehouseId,
    orderTypeCode,
    currentOrg?.id
  ]);

  useEffect(() => {
    const pickupZip = String(pickupLocation?.customer_zipcode || "").trim();
    const deliveryZip = String(deliveryLocation?.customer_zipcode || "").trim();
    const pickupAddress = String(pickupLocation?.customer_address_line_1 || "").trim();
    const deliveryAddress = String(deliveryLocation?.customer_address_line_1 || "").trim();
    if (!pickupZip || !deliveryZip || !pickupAddress || !deliveryAddress) {
      setFetchingLanes(false);
      return undefined;
    }

    if (trackingOrderId && !trackingReady) {
      setFetchingLanes(true);
      return undefined;
    }

    const { originId, destId } = resolveLaneWarehouseIds();
    if (!currentOrg?.id || !originId || !destId) {
      setFetchingLanes(false);
      return undefined;
    }

    const trackingForStops = trackingDetails || orderResponse?.linehaul_tracking_info || {};
    const skipLaneApi = hasInTransitRoutingStop(trackingDetails?.terminals, orderTypeCode)
      || isLastRoutingLegComplete(buildStops(trackingForStops, orderTypeCode || ""));

    // Last hop: origin is already the delivery warehouse. No remaining lane to fetch.
    if (String(originId) === String(destId)) {
      applyFetchedLanes([]);
      setFetchingLanes(false);
      return undefined;
    }

    const fetchKey = `${currentOrg.id}|${originId}|${destId}|${orderTypeCode || ""}|${skipLaneApi ? "locked" : "open"}`;
    if (lastLaneFetchKeyRef.current === fetchKey) {
      setLanePaths((prev) => prefixLanePaths(
        prev,
        trackingDetails?.terminals,
        orderTypeCode,
        getCompletedRouteCodes(buildStops(trackingDetails || {}, orderTypeCode || ""))
      ));
      setFetchingLanes(false);
      return undefined;
    }

    let cancelled = false;
    setFetchingLanes(true);

    if (skipLaneApi) {
      lastLaneFetchKeyRef.current = fetchKey;
      applyFetchedLanes([]);
      setFetchingLanes(false);
      return () => {
        cancelled = true;
      };
    }

    LineHaulApi.fetchLineHaulLanesJava(
      currentOrg.id,
      originId,
      destId
    ).then((result) => {
      if (cancelled) return;
      lastLaneFetchKeyRef.current = fetchKey;
      const rawPaths = result?.success && Array.isArray(result?.data?.paths)
        ? result.data.paths
        : [];
      applyFetchedLanes(rawPaths, {
        showApiWarning: rawPaths.length ? null : result?.message,
      });
    }).catch((err) => {
      if (cancelled) return;
      console.error("Failed to fetch linehaul routing paths:", err);
      applyFetchedLanes([]);
    }).finally(() => {
      if (!cancelled) setFetchingLanes(false);
    });

    return () => {
      cancelled = true;
    };
  }, [currentOrg?.id, pickupWarehouseId, propDeliveryWarehouseId, orderWarehouseId, originWarehouseId, destinationWarehouseId, originWhId, destinationWhId, orderTypeCode, originZipcode, destinationZipcode, pickupLocation?.customer_address_line_1, deliveryLocation?.customer_address_line_1, trackingOrderId, trackingReady, orderResponse?.paths]);

  useEffect(() => {
    setLanePaths((prev) => {
      if (!prev.length) return prev;
      const next = prefixLanePaths(
        prev,
        trackingDetails?.terminals,
        orderTypeCode,
        getCompletedRouteCodes(buildStops(trackingDetails || {}, orderTypeCode || ""))
      );
      const unchanged = next.every((path, idx) => path.route === prev[idx]?.route);
      return unchanged ? prev : next;
    });
  }, [trackingDetails?.terminals, orderTypeCode]);

  useEffect(() => {
    const trackingPathId = resolveTrackingPathId(trackingDetails);
    if (!trackingPathId || !lanePaths.length) return;
    const match = lanePaths.find((path) => String(path.path_id) === String(trackingPathId));
    if (!match) return;
    if (String(selectedPath) !== String(match.path_id)) {
      setSelectedPath(String(match.path_id));
      if (onSelectPath) onSelectPath(String(match.path_id));
    }
  }, [trackingDetails, lanePaths]);

  const trackingStops = buildStops(
    trackingDetails || orderResponse?.linehaul_tracking_info || {},
    orderTypeCode || ""
  );
  const completedRouteCodes = getCompletedPrefixCodes(trackingDetails?.terminals, orderTypeCode);
  const dropdownCompletedCodes = completedRouteCodes.length
    ? completedRouteCodes
    : getCompletedRouteCodes(trackingStops);
  const lastRoutingLegComplete = isLastRoutingLegComplete(trackingStops);

  const allPaths = prefixLanePaths(
    lanePaths.length > 0 ? lanePaths : transformPaths(orderResponse?.paths || []),
    trackingDetails?.terminals,
    orderTypeCode,
    dropdownCompletedCodes
  );
  const pathOptions = (Array.isArray(allPaths) ? allPaths : [])
    .map((path) => {
      if (!path) return null;
      const val = typeof path.path_id === "string" || typeof path.path_id === "number" ? String(path.path_id) : (path.id ? String(path.id) : "");
      const lbl = typeof path.route === "string" ? path.route : (path.path_name || val);
      if (!val) return null;
      return {
        value: val,
        label: String(lbl || val)
      };
    })
    .filter(Boolean);

  const activePathObj = allPaths.find((p) => String(p.path_id || p._id || p.id) === String(selectedPath)) || allPaths[0];
  const trackingPathId = resolveTrackingPathId(trackingDetails);
  const selectedADifferentPath = Boolean(
    selectedPath && trackingPathId && String(selectedPath) !== String(trackingPathId)
  );
  const legsForDates = (trackingDetails?.legs?.length > 0
    ? trackingDetails.legs
    : activePathObj?.legs) || [];
  const terminalDates = buildLegDatesFromTerminals(
    trackingDetails?.terminals,
    legsForDates
  );
  const resolveConnectorDate = (connectorIndex) => {
    if (legDatesMap[connectorIndex]) {
      return legDatesMap[connectorIndex];
    }
    if (userTouchedLegDates || selectedADifferentPath) {
      return null;
    }
    return terminalDates[connectorIndex] || null;
  };
  // Existing orders keep current tracking until Save. Create (no tracking) still previews the selected lane.
  const timelineStops = trackingStops.length
    ? trackingStops
    : overlayTrackingOnLaneStops(
      buildStopsFromLane(activePathObj, orderTypeCode || ""),
      trackingStops
    );

  const trackingInfo = orderResponse?.linehaul_tracking_info;
  const activeTracking = trackingDetails || trackingInfo || {};
  const statusKeyRaw = String(activeTracking.current_lh_status || "").toUpperCase();
  const orderStatusRaw = String(activeTracking.status || orderResponse?.status || "").toUpperCase();
  const statusKey = STATUS_KEY[statusKeyRaw]
    || STATUS_KEY[orderStatusRaw]
    || activeTracking.status?.toLowerCase()
    || "pending";
  const { label: statusLabel, icon: PillIcon, color: statusColor } = LEG_STATUS[statusKey] || LEG_STATUS.pending;

  const hasPathOptions = pathOptions.length > 0;
  const showLinehaulTimeline = timelineStops.length > 0;
  const routingPathLockReason = remainingLaneLockReason(
    trackingDetails?.terminals,
    orderTypeCode,
    lastRoutingLegComplete
  );
  const isRoutingPathDisabled = disabled
    || fetchingLanes
    || lastRoutingLegComplete
    || hasInTransitRoutingStop(trackingDetails?.terminals, orderTypeCode);
  const linehaulTariffEditLocked = orgSettings?.service_level_flow_enabled === "true"
    && !checkServiceExistance("OLOS")
    && isLinehaulTariffLockedByServiceLevel(serviceLevelLegLos, losOptions);
  const isLinehaulTariffDisabled = disabled || fetchingLanes || linehaulTariffEditLocked;
  const isTariffOrSpecialDisabled = disabled || fetchingLanes;

  const titleNode = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
      <LinehaulTrackIcon style={{ fontSize: "24px" }} />
      <span style={{ color: "var(--Gray-Shade-9, var(--Colors-Shared-Gray-Shade-900, #111314))" }}>Linehaul</span>
    </span>
  );

  const statusPill = (trackingInfo || showLinehaulTimeline || hasPathOptions) ? (
    <span className={`lh-leg-chip lh-leg-chip-${statusKey}`}>
      <PillIcon width="16" height="16" color={statusColor} />
      {statusLabel}
    </span>
  ) : null;

  const extraActionNode = (
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <label className="lh-leg-header-special">
        <Checkbox
          checked={lhSpecial}
          onChange={(e) => {
            const checked = Boolean(e.target.checked);
            setLhSpecial(checked);
            if (onChangeSpecial) onChangeSpecial(checked);
          }}
          disabled={isTariffOrSpecialDisabled}
        />
        Special
      </label>

      {statusPill}
    </div>
  );

  const savedPathIdForSelect = resolveTrackingPathId(trackingDetails) || initialPathId;
  const safeSelectedPath = (
    (typeof selectedPath === "string" || typeof selectedPath === "number")
    && pathOptions.some((opt) => String(opt.value) === String(selectedPath))
      ? String(selectedPath)
      : (savedPathIdForSelect && pathOptions.some((opt) => String(opt.value) === String(savedPathIdForSelect))
        ? String(savedPathIdForSelect)
        : pathOptions[0]?.value)
  ) || undefined;

  useEffect(() => {
    if (!onChangeTracking) return;

    const selectedTariffObj = selectedTariffId
      ? losOptions.find((item) => String(item.id) === String(selectedTariffId))
      : null;
    let tariffData = buildLinehaulTariffPayload(selectedTariffObj);
    if (!tariffData && orderLinehaulTariffId && !slDrivenTariffRef.current) {
      const fallbackOpt = losOptions.find(
        (item) => String(item.id) === String(orderLinehaulTariffId)
      );
      tariffData = buildLinehaulTariffPayload(fallbackOpt) || {
        los_id: orderLinehaulTariffId,
        los_name: "",
        los_code: "",
        rate_sheet_id: "",
      };
    }

    /* PREVIOUS PAYLOAD FORMATION (COMMENTED OUT):
    const formattedLegs = (activePathObj?.legs || []).map((leg, idx) => {
      const connectorIndex = idx + 1;
      const legDate = legDatesMap[connectorIndex];
      let formattedDate = null;
      if (legDate) {
        formattedDate = moment.isMoment(legDate) ? legDate.format("YYYY-MM-DD") : moment(legDate).format("YYYY-MM-DD");
      } else if (leg.linehaul_service_date) {
        formattedDate = leg.linehaul_service_date;
      }

      return {
        routing_id: String(leg.routing_id || leg._id || leg.id || ""),
        origin_id: String(leg.origin_id || ""),
        origin_code: String(leg.origin_code || ""),
        destination_id: String(leg.destination_id || ""),
        destination_code: String(leg.destination_code || ""),
        operational_days: Array.isArray(leg.operational_days) ? leg.operational_days : [],
        ...(formattedDate ? { linehaul_service_date: formattedDate } : {})
      };
    });

    const payload = {
      path_name: String(activePathObj?.route || activePathObj?.path_name || ""),
      path_id: String(activePathObj?.path_id || selectedPath || ""),
      legs: formattedLegs,
      is_special: Boolean(lhSpecial),
      tariff: tariffData
    };
    */

    // Payload legs + hop dates (same rules as LhTrackingCard save): use path/tracking
    // legs when present; otherwise build rows from legDatesMap + warehouse terminals
    // (NLH mid-route lanes often have no path.legs). Map each row's date via hop key
    // from terminals — not resolvePayloadLegDateKey / timeline slice.
    const trackingTerminals = trackingDetails?.terminals
      || orderResponse?.linehaul_tracking_info?.terminals
      || [];
    let legsForPayload = (activePathObj?.legs?.length
      ? activePathObj.legs
      : (trackingDetails?.legs?.length
        ? trackingDetails.legs
        : (orderResponse?.linehaul_tracking_info?.legs || [])));
    if (!legsForPayload.length && Object.keys(legDatesMap).length) {
      const warehouses = filterLhWarehouseTerminals(trackingTerminals);
      legsForPayload = Object.keys(legDatesMap)
        .map((key) => Number(key))
        .filter((connectorIndex) => connectorIndex > 0 && formatCalendarDate(legDatesMap[connectorIndex]))
        .sort((a, b) => a - b)
        .map((connectorIndex) => {
          const legIdx = connectorIndex - 1;
          const originTerminal = warehouses[legIdx];
          const destTerminal = warehouses[connectorIndex] || warehouses[legIdx + 1];
          return {
            connectorIndex,
            routing_id: "",
            origin_id: String(originTerminal?.warehouse_id || destTerminal?.warehouse_id || ""),
            origin_code: String(originTerminal?.warehouse_code || originTerminal?.warehouse_name || ""),
            destination_id: String(destTerminal?.warehouse_id || ""),
            destination_code: String(destTerminal?.warehouse_code || destTerminal?.warehouse_name || ""),
          };
        })
        .filter((leg) => leg.origin_code || leg.destination_code);
    }
    const formattedLegs = legsForPayload.map((leg, idx) => {
      const connectorIndex = resolveHopDateKeyForLeg(trackingTerminals, leg)
        || leg.connectorIndex
        || (idx + 1);
      const formattedDate = formatCalendarDate(resolveConnectorDate(connectorIndex));

      return {
        routing_id: String(leg.routing_id || leg._id || leg.id || ""),
        origin_id: String(leg.origin_id || ""),
        origin_code: String(leg.origin_code || ""),
        destination_id: String(leg.destination_id || ""),
        destination_code: String(leg.destination_code || ""),
        ...(formattedDate ? { linehaul_service_date: formattedDate } : {})
      };
    });

    const serviceDateValue = serviceDate
      || resolveConnectorDate(1)
      || Object.values(terminalDates).find(Boolean)
      || Object.values(legDatesMap).find(Boolean);
    const formattedServiceDate = serviceDateValue
      ? (moment.isMoment(serviceDateValue) ? serviceDateValue.format("YYYY-MM-DD") : moment(serviceDateValue).format("YYYY-MM-DD"))
      : null;

    const payload = {
      path_id: String(activePathObj?.path_id || selectedPath || ""),
      legs: formattedLegs,
      tariff: tariffData,
      linehaul_special: Boolean(lhSpecial),
      ...(formattedServiceDate ? { service_date: formattedServiceDate } : {})
    };

    const payloadKey = JSON.stringify(payload);
    if (lastTrackingPayloadRef.current === payloadKey) return;
    lastTrackingPayloadRef.current = payloadKey;

    onChangeTracking(payload);
  }, [selectedPath, selectedTariffId, legDatesMap, lanePaths, losOptions, serviceDate, trackingDetails, userTouchedLegDates, lhSpecial, orderLinehaulTariffId]);

  useEffect(() => {
    if (onChangeSpecial) onChangeSpecial(Boolean(lhSpecial));
  }, [lhSpecial, onChangeSpecial]);

  return (
    <SectionCard
      title={titleNode}
      extraActionNode={extraActionNode}
    >
      <div className={`lh-leg-card${fetchingLanes ? " is-saving" : ""}`} style={{ boxShadow: "none", border: "none", padding: 0, minHeight: fetchingLanes ? 140 : undefined }}>
        {fetchingLanes && (
          <div className="lh-leg-card-saving" aria-live="polite" aria-busy="true">
            <Spin tip="Loading routing paths..." />
          </div>
        )}
        {/* Top Controls Row: Routing Path (Left) & Tariff (Right) */}
        <div className="lh-leg-top-controls">
          <div className="lh-leg-field lh-leg-field-routing">
            <label className="lh-leg-field-label">Routing Path</label>
            <div className="lh-leg-routing-input-wrap">
              <Tooltip title={routingPathLockReason || ""}>
                <span style={{ display: "block" }}>
              <Select
                value={safeSelectedPath}
                onChange={(val) => {
                  const strVal = typeof val === "string" || typeof val === "number" ? String(val) : "";
                  setUserTouchedLegDates(false);
                  manualFirstLegEditRef.current = false;
                  lastPickupSyncedKeyRef.current = null;
                  prevPickupDateKeyRef.current = undefined;
                  appliedApiLegDatesRef.current = false;
                  savedLegDatesSnapshotRef.current = {};
                  initialPickupDateKeyRef.current = null;
                  setSelectedPath(strVal);
                  if (onSelectPath) onSelectPath(strVal);
                }}
                placeholder={fetchingLanes ? "Loading routes..." : "--Select--"}
                disabled={isRoutingPathDisabled}
                className="lh-tracking-select"
                dropdownClassName="lh-routing-path-dropdown"
              >
                {pathOptions.map((opt, idx) => (
                  <Select.Option
                    key={String(opt.value || idx)}
                    value={String(opt.value)}
                    title={String(opt.label)}
                  >
                    {renderRoutingPathLabel(opt.label, dropdownCompletedCodes)}
                  </Select.Option>
                ))}
              </Select>
                </span>
              </Tooltip>
            </div>
          </div>

          <div className="lh-leg-field lh-leg-field-tariff">
            <label className="lh-leg-field-label">
              <sup className="lh-required-star">*</sup>
              Tariff
            </label>
            <Select_v2
              key={`lh-tariff-${accountId || "none"}`}
              data-testid="linehaul-tariff-dropdown"
              labelField="name"
              valueField="id"
              codeField="code"
              groupField="los_group"
              options={losOptions}
              value={selectedTariffId != null && selectedTariffId !== "" ? String(selectedTariffId) : undefined}
              onChange={(value, option) => {
                if (!value) return;
                userTouchedTariffRef.current = true;
                const nextId = String(value);
                setSelectedTariffId(nextId);
                if (onSelectLos) onSelectLos(option?.code || nextId);
              }}
              placeholder="--Select--"
              showSelectOption={true}
              disabled={isLinehaulTariffDisabled}
              className="lh-tracking-select"
              dropdownMatchSelectWidth={false}
            />
          </div>
        </div>

        {/* Timeline Section with Dynamic Leg Splitting */}
        {showLinehaulTimeline && (
          <div className="lh-leg-timeline-scroll" style={{ marginTop: "24px" }}>
          <div className="lh-leg-timeline">
            {timelineStops.map((stop, index) => {
              const legDateKey = resolveRoutingLegKey(timelineStops, index);
              const isIntermediateLeg = shouldShowLegDatePill(
                index,
                timelineStops.length,
                orderTypeCode || "",
                timelineStops
              );
              const legData = (legDateKey != null
                ? activePathObj?.legs?.[legDateKey - 1]
                : null)
                || {};
              const operationalDays = legData.operational_days || [];
              const sortedTerminals = [...(trackingDetails?.terminals || [])].sort(
                (a, b) => (a.sequence_no ?? 0) - (b.sequence_no ?? 0)
              );
              const apiServiceDate = resolveLegLinehaulServiceDate(
                sortedTerminals,
                legDateKey,
                legData
              ) || null;
              const pickupRevised = Boolean(
                initialPickupDateKeyRef.current
                && pickupDateKey
                && pickupDateKey !== initialPickupDateKeyRef.current
              );
              const pickupFirstLegFallback = legDateKey === 1 && pickupDateMoment && pickupRevised
                ? resolveFirstLegDateFromPickup(pickupDateMoment, legData)
                : null;
              const currentLegDate = (legDateKey != null ? resolveConnectorDate(legDateKey) : null)
                || pickupFirstLegFallback
                || toStartOfDay(apiServiceDate);

              const datePill = isIntermediateLeg ? (
                <LegDatePillPicker
                  value={currentLegDate}
                  disabled={disabled || isLegDateLocked(stop)}
                  operationalDays={operationalDays}
                  minDate={pickupDateMoment}
                  onChange={(newDate) => {
                    const storeKey = legDateKey != null ? legDateKey : index;
                    const currentKey = formatCalendarDate(
                      legDateKey != null ? resolveConnectorDate(legDateKey) : null
                    );
                    const newKey = formatCalendarDate(newDate);
                    if (newKey === currentKey) return;

                    setUserTouchedLegDates(true);
                    if (storeKey === 1) {
                      manualFirstLegEditRef.current = true;
                      lastPickupSyncedKeyRef.current = null;
                    }
                    setLegDatesMap((prev) => {
                      const next = { ...prev };
                      next[storeKey] = newDate;
                      return next;
                    });
                  }}
                />
              ) : null;

              return (
                <React.Fragment key={`${stop.name}-${index}`}>
                  <Stop
                    stop={stop}
                    align={index === 0 ? "start" : index === timelineStops.length - 1 ? "end" : "center"}
                  />
                  {index < timelineStops.length - 1 && (
                    <LegConnector
                      leg={stop.leg || { state: "pending", progress: 0 }}
                      datePill={datePill}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          </div>
        )}
      </div>
    </SectionCard>
  );
};

export default LineHaulCreateForm;
